import React, { useState, useEffect, useCallback } from 'react';
import {
  MdInfo,
  MdLink,
  MdWarning,
  MdNotifications,
  MdEdit,
  MdMenuBook,
  MdChecklist,
  MdChat,
  MdStar,
  MdSmartToy,
  MdCreate,
  MdDescription,
  MdFeedback,
  MdSchool,
  MdSwapHoriz
} from 'react-icons/md';
import { useClass } from '../utils/ClassContext';
import { api } from '../utils/api';
import { canManageClasses } from '../utils/permissions';

const ClassHome = ({
  username,
  userRole,
  userName,
  apiKey,
  notificationCount,
  hasConsent,
  onNavigate,
  apiBaseUrl,
  isPublicView = false
}) => {
  const { selectedClass, clearClass, hasClassSelected, isPublicView: contextIsPublicView } = useClass();
  const isPublic = isPublicView || contextIsPublicView || userRole === 'Public';
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  // TODO(human): Add session validation function here
  // Create a function called validateUserSession() that:
  // - Checks if username exists in localStorage
  // - Verifies that required authentication data is present
  // - Returns true if session is valid, false otherwise
  // - Handle any edge cases where localStorage might be undefined
  // Calculate quota status
  const validateUserSession = useCallback(() => {
    // Step 1: Check if localStorage is available (edge case handling)
    if (typeof localStorage === 'undefined') {
      return false;
    }

    // Step 2: Get username from localStorage
    const storedUsername = localStorage.getItem('username');

    // Step 3: Basic validation checks
    if (!storedUsername || storedUsername.trim() === '') {
      return false;
    }

    // Step 4: Check if it matches the prop username (consistency check)
    if (storedUsername !== username) {
      return false;
    }

    // Step 5: Validate email format (basic check)
    if (!storedUsername.includes('@')) {
      return false;
    }

    // Step 6: Check for other required auth data
    const storedUserRole = localStorage.getItem('userRole');
    if (!storedUserRole) {
      return false;
    }

    // Step 7: Optional - check if user role matches prop
    if (storedUserRole !== userRole) {
      return false;
    }

    // Step 8: All checks passed
    return true;
  }, [username, userRole]);

  // Fetch recent activity data from API
  useEffect(() => {
    const fetchRecentActivity = async () => {
      // Only fetch data if user has selected a specific class and is not an admin
      if (!username || !apiBaseUrl || !userRole || !hasClassSelected() || canManageClasses(userRole)) {
        setLoading(false);
        return;
      }

      // Validate user session before making API calls
      const sessionValid = validateUserSession();
      console.log(`[ClassHome] Session validation result: ${sessionValid}`);
      if (!sessionValid) {
        console.log('Invalid user session - skipping API calls');
        setLoading(false);
        return;
      }

      try {
        const classContext = selectedClass && selectedClass.class_id !== 'all' ? {
          class_id: selectedClass.class_id,
          class_code: selectedClass.class_code
        } : null;

        // Recent Activity now only shows notifications (not user's own feedback)
        const activities = [];

        // Fetch notifications for all users (students, TAs, instructors)
        try {
          const notificationsResponse = await api.getUserNotifications(username, classContext);

          // Process notifications data if available
          if (notificationsResponse && notificationsResponse.notifications) {
            notificationsResponse.notifications.slice(0, 10).forEach(notification => {
              if (notification.type === 'ai_feedback') {
                activities.push({
                  type: 'ai_feedback_received',
                  title: 'AI Feedback Available',
                  description: notification.text,
                  timestamp: notification.timestamp,
                  environment: null,
                  relationship: null,
                  feedbackId: notification.specifying_id,
                  notificationId: notification.notification_id
                });
              } else if (notification.type === 'discussion') {
                activities.push({
                  type: 'discussion_comment',
                  title: 'New Discussion Comment',
                  description: notification.text,
                  timestamp: notification.timestamp,
                  environment: null,
                  relationship: null,
                  feedbackId: notification.specifying_id,
                  notificationId: notification.notification_id
                });
              } else if (notification.type === 'evaluation') {
                activities.push({
                  type: 'evaluation_received',
                  title: 'Feedback Evaluated',
                  description: notification.text,
                  timestamp: notification.timestamp,
                  environment: null,
                  relationship: null,
                  feedbackId: notification.specifying_id,
                  notificationId: notification.notification_id
                });
              } else if (notification.type === 'environment_release') {
                const environmentName = notification.content?.environment || notification.specifying_id || null;
                console.log('[ClassHome] Environment release notification:', {
                  notificationId: notification.notification_id,
                  content: notification.content,
                  specifying_id: notification.specifying_id,
                  extractedEnvironment: environmentName
                });
                activities.push({
                  type: 'environment_release',
                  title: 'New Environment Released',
                  description: notification.text,
                  timestamp: notification.timestamp,
                  environment: environmentName,
                  relationship: null,
                  notificationId: notification.notification_id
                });
              } else {
                activities.push({
                  type: 'notification',
                  title: 'Notification',
                  description: notification.text,
                  timestamp: notification.timestamp,
                  environment: null,
                  relationship: null,
                  notificationId: notification.notification_id
                });
              }
            });
          }
        } catch (notificationError) {
          console.log('Notifications API not available or user not authenticated:', notificationError.message);
          // Continue without notification data
        }

        // Sort activities by timestamp (most recent first)
        activities.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

        setRecentActivity(activities);
      } catch (error) {
        console.error('Error fetching recent activity:', error);
        // Set empty activity array on general error
        setRecentActivity([]);
      } finally {
        setLoading(false);
      }
    };

    fetchRecentActivity();
  }, [username, apiBaseUrl, userRole, selectedClass, validateUserSession, hasClassSelected]);

  // Get time-based greeting
  const getTimeBasedGreeting = () => {
    const currentHour = new Date().getHours();

    if (currentHour >= 5 && currentHour < 12) {
      return "Good morning";
    } else if (currentHour >= 12 && currentHour < 17) {
      return "Good afternoon";
    } else {
      return "Good evening";
    }
  };

  // Quick action handlers
  const handleQuickAction = (action) => {
    onNavigate(action);
  };

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = now - date;

    // Handle negative differences (future dates)
    if (diffInMs < 0) {
      return 'Just now';
    }

    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    if (diffInMinutes < 1) {
      return 'Just now';
    } else if (diffInMinutes < 60) {
      return `${diffInMinutes} minute${diffInMinutes === 1 ? '' : 's'} ago`;
    } else if (diffInHours < 24) {
      return `${diffInHours} hour${diffInHours === 1 ? '' : 's'} ago`;
    } else if (diffInDays === 1) {
      return 'Yesterday';
    } else if (diffInDays < 7) {
      return `${diffInDays} days ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  // Get activity icon based on type
  const getActivityIcon = (type) => {
    switch (type) {
      case 'ai_feedback_received':
        return <MdSmartToy className="text-blue-600" />;
      case 'feedback_received':
        return <MdSmartToy className="text-blue-600" />;
      case 'feedback_rated':
        return <MdStar className="text-yellow-600" />;
      case 'evaluation_received':
        return <MdChecklist className="text-orange-600" />;
      case 'discussion_comment':
        return <MdChat className="text-purple-600" />;
      case 'environment_release':
        return <MdSchool className="text-green-600" />;
      case 'erd_created':
        return <MdCreate className="text-green-600" />;
      case 'notification':
        return <MdNotifications className="text-gray-600" />;
      default:
        return <MdDescription className="text-gray-600" />;
    }
  };

  // Get activity color based on type
  const getActivityColor = (type) => {
    switch (type) {
      case 'ai_feedback_received':
        return 'bg-blue-50 border-blue-200';
      case 'feedback_received':
        return 'bg-blue-50 border-blue-200';
      case 'feedback_rated':
        return 'bg-yellow-50 border-yellow-200';
      case 'evaluation_received':
        return 'bg-orange-50 border-orange-200';
      case 'discussion_comment':
        return 'bg-purple-50 border-purple-200';
      case 'environment_release':
        return 'bg-green-50 border-green-200';
      case 'erd_created':
        return 'bg-green-50 border-green-200';
      case 'notification':
        return 'bg-gray-50 border-gray-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };

  // Handle activity click
  const handleActivityClick = (activity) => {
    if (activity.type === 'ai_feedback_received' || activity.type === 'feedback_received' || activity.type === 'feedback_rated') {
      onNavigate('discussions', { feedbackId: activity.feedbackId });
    } else if (activity.type === 'evaluation_received') {
      onNavigate('discussions', { feedbackId: activity.feedbackId });
    } else if (activity.type === 'discussion_comment') {
      onNavigate('discussions', { feedbackId: activity.feedbackId });
    } else if (activity.type === 'environment_release') {
      // Extract environment from activity data
      const environmentToSelect = activity.environment || activity.notificationId || 'None';
      console.log('[ClassHome] Environment release clicked, environment:', environmentToSelect);
      onNavigate('editor');
    } else if (activity.type === 'erd_created') {
      onNavigate('editor');
    } else {
      onNavigate('notifications'); // For all other notifications, go to notifications page
    }
  };

  return (
    <div className="space-y-6">
      {/* Welcome Header */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="flex items-center justify-between">
          <div>
            {!isPublic ? (
              <>
                <h1 className="text-3xl font-bold text-gray-800 mb-2">
                  {getTimeBasedGreeting()}, {userName.firstName}!
                </h1>
                <p className="text-gray-600">
                  Ready to work on your Entity-Relationship Diagrams?
                </p>
              </>
            ) : (
              <>
                <h1 className="text-3xl font-bold text-gray-800 mb-2">
                  {selectedClass ? selectedClass.class_name : 'Public Class'}
                </h1>
                <p className="text-gray-600">
                  View-only mode: You are viewing this class as a public viewer.
                </p>
              </>
            )}
            {selectedClass && (
              <div className="mt-3 flex items-center gap-2 text-sm text-gray-600">
                <MdSchool className="text-blue-600" />
                <span>
                  <strong>{selectedClass.class_name}</strong> ({selectedClass.class_code})
                </span>
                <span>•</span>
                <span>{selectedClass.semester} {selectedClass.year}</span>
                {!isPublic && (
                  <button
                    onClick={() => { clearClass(); onNavigate('dashboard'); }}
                    className="ml-2 text-blue-600 hover:text-blue-800 transition-colors"
                    title="Switch Class"
                  >
                    <MdSwapHoriz />
                  </button>
                )}
              </div>
            )}
          </div>
          <div className="hidden md:block">
            <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-red-500 rounded-full flex items-center justify-center">
              <span className="text-white text-2xl font-bold">ERD</span>
            </div>
          </div>
        </div>
      </div>

      {/* Info Alert */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <MdInfo className="text-2xl text-blue-600" />
          </div>
          <div className="ml-3">
            <h3 className="text-lg font-medium text-blue-800 mb-2">
              AI Assistant for {selectedClass ? selectedClass.class_name : 'Your Class'}
            </h3>
            <div className="text-blue-700 space-y-2">
              <p>
                This is your AI Assistant for {selectedClass ? selectedClass.class_name : 'your class'}. Please use it at your discretion.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Status Cards - Flexible Flexbox */}
      <div className="flex flex-col sm:flex-row gap-6">

        {/* API Status Card */}
        <div className="flex-1 bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center">
            <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
              isPublic 
                ? 'bg-gray-100' 
                : apiKey 
                  ? 'bg-green-100' 
                  : 'bg-yellow-100'
              }`}>
              {isPublic ? (
                <MdWarning className="text-2xl text-gray-600" />
              ) : apiKey ? (
                <MdLink className="text-2xl text-green-600" />
              ) : (
                <MdWarning className="text-2xl text-yellow-600" />
              )}
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-medium text-gray-800">
                API Status
              </h3>
              <p className={`text-sm ${
                isPublic 
                  ? 'text-gray-600' 
                  : apiKey 
                    ? 'text-green-600' 
                    : 'text-yellow-600'
                }`}>
                {isPublic ? 'Not Signed In' : apiKey ? 'Connected' : 'Not Connected'}
              </p>
            </div>
          </div>
          {isPublic ? (
            <div className="mt-4 p-3 bg-gray-50 rounded-md">
              <p className="text-sm text-gray-700">
                Sign in to access AI feedback features on your ERD diagrams.
              </p>
            </div>
          ) : !apiKey && (
            <div className="mt-4 p-3 bg-yellow-50 rounded-md">
              <p className="text-sm text-yellow-700">
                You need an OpenAI API Key to receive AI feedback on your ERD diagrams.
              </p>
            </div>
          )}
        </div>

        {/* Notifications Card */}
        <div className="flex-1 bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <MdNotifications className="text-2xl text-blue-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-medium text-gray-800">
                Notifications
              </h3>
              <p className="text-sm text-gray-600">
                {notificationCount > 0 ? (
                  <span className="text-blue-600 font-medium">
                    {notificationCount} unseen
                  </span>
                ) : (
                  'All caught up!'
                )}
              </p>
            </div>
          </div>
          {notificationCount > 0 && (
            <button
              onClick={() => handleQuickAction('notifications')}
              className="mt-4 w-full bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md text-sm transition-colors duration-200"
            >
              View Notifications
            </button>
          )}
        </div>
      </div>

      {/* API Key Information - Only show if user has no API key AND no consent */}
      {!apiKey && !hasConsent && (
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Getting Started with AI Feedback</h2>
          <div className="prose max-w-none text-gray-600">
            <p className="mb-4">
              To use the AI Assistant, you have two options:
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-blue-50 rounded-lg p-4">
                <h3 className="font-semibold text-blue-800 mb-2">Option 1: Sign Consent Form</h3>
                <p className="text-sm text-blue-700 mb-3">
                  Participate in our research study to get free access to AI feedback.
                </p>
                <button
                  onClick={() => handleQuickAction('consent')}
                  className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded text-sm transition-colors duration-200"
                >
                  View Consent Form
                </button>
              </div>
              <div className="bg-green-50 rounded-lg p-4">
                <h3 className="font-semibold text-green-800 mb-2">Option 2: Use Your API Key</h3>
                <p className="text-sm text-green-700 mb-3">
                  Provide your own OpenAI API key to access AI feedback features.
                </p>
                <button
                  onClick={() => handleQuickAction('api-key-form')}
                  className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded text-sm transition-colors duration-200"
                >
                  Enter API Key
                </button>
              </div>
            </div>
            <div className="mt-4 p-4 bg-yellow-50 rounded-lg">
              <p className="text-sm text-yellow-800">
                <strong>Note:</strong> If you're under 18, you don't need to sign the consent form.
                Please contact your instructor for free access.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Consent Acknowledged - API Key Loading */}
      {hasConsent && !apiKey && (
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">AI Access Granted</h2>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center">
              <MdSmartToy className="text-green-600 text-2xl mr-3" />
              <div>
                <h3 className="font-semibold text-green-800 mb-1">Thank you for your consent!</h3>
                <p className="text-sm text-green-700">
                  Your AI access is being activated. Please refresh the page in a moment to start using AI feedback.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClassHome;